import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Aperture,
  Contrast,
  Crop,
  Download,
  Filter,
  FlipHorizontal2,
  FlipVertical2,
  Palette,
  RotateCcw,
  RotateCw,
  Save,
  Sun,
  ScanFace,
  Thermometer,
  X,
  Zap,
} from 'lucide-react'
import { ZoomableImage } from '../../components/ui/ZoomableImage'
import { StudioCropOverlay } from './StudioCropOverlay'
import {
  getProjectStyleTitle,
  formatProductionDate,
  type StudioImageDetailItem,
} from '../lib/studioImageDetails'
import '../../styles/visual-style-modal.css'
import { haptic } from '../../lib/native'
import {
  downloadStudioImage,
  saveStudioImageToGallery,
  analyzeStudioImageFaces,
  STUDIO_BEAUTY_PRESETS,
  renderStudioImageToCanvas,
  renderStudioImageBlob,
  releaseStudioImagePreviewSource,
  getStudioLookThumbnails,
  type StudioImageEdits,
} from '../lib/studioImageDownload'
import { STUDIO_LOOKS, STUDIO_FILTER_CATEGORIES, type StudioFilterCategory } from '../lib/studioImageFilterEngine'
import { useUiText } from '../../i18n/ui'
import { studioMessages } from '../../i18n/studio'
import { VoxnelaMark } from '../../components/ui/VoxnelaMark'
import { VoxnelaAlertModal } from '../../components/ui/VoxnelaAlertModal'
import { saveStudioImageEdits } from '../lib/studioApi'

export type { StudioImageDetailItem }

export interface StudioImageDetailSheetProps {
  open: boolean
  items: StudioImageDetailItem[]
  initialIndex?: number
  onClose: () => void
  onEdit: (item: StudioImageDetailItem) => void
}

type EditorTool = 'beauty' | 'filters' | 'light' | 'contrast' | 'color' | 'temperature' | 'tint' | 'detail' | 'effects' | 'vignette' | 'crop' | 'rotate' | 'flip' | 'reset'
type BeautyCategory = 'auto' | 'skin' | 'blemish' | 'underEye' | 'eyes' | 'teeth' | 'lips' | 'faceLight' | 'shine' | 'glow'

const emptyEdits: StudioImageEdits = {
  filter: 'original',
  filterIntensity: 100,
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  sharpness: 0,
  beautyEnabled: false,
  beautyPreset: 'natural',
  beautyIntensity: 70,
  beautyTouched: {},
  blemishSpots: [],
  blemishBrushSize: 18,
  beauty: {
    skinSmooth: 0,
    skinTexture: 0,
    skinTone: 0,
    skinRedness: 0,
    skinVitality: 0,
    blemish: 0,
    underEye: 0,
    eyeBrightness: 0,
    eyeWhitening: 0,
    eyeClarity: 0,
    eyeVitality: 0,
    teethWhitening: 0,
    lipVitality: 0,
    lipBrightness: 0,
    faceLight: 0,
    faceShadows: 0,
    faceHighlights: 0,
    faceTemperature: 0,
    shineReduction: 0,
    glow: 0,
    glowSpread: 0,
  },
  vignette: 0,
  vignetteFeather: 60,
  tint: 0,
  highlights: 0,
  shadows: 0,
  fade: 0,
  grain: 0,
  grainSize: 1,
  cropRatio: '',
  cropZoom: 1,
  cropPanX: 0,
  cropPanY: 0,
  cropFrameWidth: 1,
  cropFrameHeight: 1,
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
}

const hasPreviewEdits = (edits: StudioImageEdits) => edits.filter !== 'original'
  || edits.filterIntensity !== 100
  || edits.brightness !== 0 || edits.contrast !== 0 || edits.saturation !== 0
  || edits.beautyEnabled || Object.values(edits.beauty).some((value) => value !== 0)
  || edits.blemishSpots.length > 0
  || edits.warmth !== 0 || edits.sharpness !== 0 || edits.vignette !== 0
  || edits.tint !== 0 || edits.highlights !== 0 || edits.shadows !== 0
  || edits.fade !== 0 || edits.grain !== 0
  || Boolean(edits.cropRect && (edits.cropRect.x > 0.005 || edits.cropRect.y > 0.005 || edits.cropRect.width < 0.995 || edits.cropRect.height < 0.995))
  || (Boolean(edits.cropRatio) && edits.cropRatio !== 'free')
  || edits.rotation !== 0 || edits.flipHorizontal || edits.flipVertical

const cropOptions = [
  { value: '', label: 'Serbest' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
]

const editorLabels: Record<string, Record<EditorTool, string>> = {
  tr: { beauty: 'Güzelleştir', filters: 'Filtre', light: 'Işık', contrast: 'Kontrast', color: 'Renk', temperature: 'Sıcaklık', tint: 'Ton', detail: 'Detay', effects: 'Efekt', vignette: 'Vinyet', crop: 'Kırp', rotate: 'Döndür', flip: 'Yansıt', reset: 'Sıfırla' },
  en: { beauty: 'Retouch', filters: 'Looks', light: 'Light', contrast: 'Contrast', color: 'Color', temperature: 'Temperature', tint: 'Tint', detail: 'Detail', effects: 'Effects', vignette: 'Vignette', crop: 'Crop', rotate: 'Rotate', flip: 'Flip', reset: 'Reset' },
}

const editorToolIcons = {
  beauty: ScanFace,
  filters: Filter,
  light: Sun,
  contrast: Contrast,
  color: Palette,
  temperature: Thermometer,
  tint: Palette,
  detail: Zap,
  effects: Aperture,
  vignette: Aperture,
  crop: Crop,
  rotate: RotateCw,
  flip: FlipHorizontal2,
  reset: RotateCcw,
} satisfies Record<EditorTool, typeof Filter>

const beautyToolIcons = {
  auto: Sun,
  skin: ScanFace,
  blemish: Zap,
  underEye: Aperture,
  eyes: Aperture,
  teeth: Sun,
  lips: Palette,
  faceLight: Sun,
  shine: Contrast,
  glow: Aperture,
} satisfies Record<BeautyCategory, typeof Filter>

export function StudioImageDetailSheet({
  open,
  items,
  initialIndex = 0,
  onClose,
}: StudioImageDetailSheetProps) {
  const { locale, text } = useUiText()
  const copy = studioMessages[locale]
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSavingToGallery, setIsSavingToGallery] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savingMode, setSavingMode] = useState<'overwrite' | 'copy' | null>(null)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [activeTool, setActiveTool] = useState<EditorTool | null>(null)
  const [activeBeautyCategory, setActiveBeautyCategory] = useState<BeautyCategory | null>(null)
  const [faceAnalysisState, setFaceAnalysisState] = useState<'idle' | 'loading' | 'ready' | 'none' | 'error'>('idle')
  const [manualBlemishMode, setManualBlemishMode] = useState(false)
  const [edits, setEdits] = useState<StudioImageEdits>(emptyEdits)
  const [previewReadyFor, setPreviewReadyFor] = useState<string | null>(null)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const previewGenerationRef = useRef(0)
  const [showOriginalWhilePressed, setShowOriginalWhilePressed] = useState(false)
  const originalPressTimerRef = useRef<number | null>(null)
  const originalPressStartRef = useRef<{ x: number; y: number } | null>(null)
  const [lookThumbnails, setLookThumbnails] = useState<{ itemId: string; urls: Record<string, string> } | null>(null)
  const [activeFilterCategory, setActiveFilterCategory] = useState<StudioFilterCategory>('retro')
  const [cropPreviewSize, setCropPreviewSize] = useState<{ width: number; height: number; key: string } | null>(null)
  const [prevOpenState, setPrevOpenState] = useState<{ open: boolean; initialIndex: number }>({
    open,
    initialIndex,
  })
  const [previousEditorTarget, setPreviousEditorTarget] = useState({ open, activeIndex })

  // Adjust state during render when open or initialIndex changes (prevents cascading render lint error)
  if (prevOpenState.open !== open || prevOpenState.initialIndex !== initialIndex) {
    setPrevOpenState({ open, initialIndex })
    if (open) {
      const safeIdx = Math.max(0, Math.min(items.length - 1, initialIndex))
      setActiveIndex(safeIdx)
    }
  }

  const trackRef = useRef<HTMLDivElement>(null)
  const previewSlideRef = useRef<HTMLElement>(null)
  const activeFrameRef = useRef<HTMLDivElement>(null)
  const scrollFrameRef = useRef<number | null>(null)
  const cropTransformRef = useRef({ scale: 1, position: { x: 0, y: 0 } })
  const [zoomTransform, setZoomTransform] = useState({ scale: 1, position: { x: 0, y: 0 } })

  const handleZoomTransformChange = useCallback((transform: { scale: number; position: { x: number; y: number } }) => {
    cropTransformRef.current = transform
    setZoomTransform(transform)
  }, [])

  const handleCropChange = useCallback((newRect: { x: number; y: number; width: number; height: number }) => {
    setEdits((current) => {
      if (
        current.cropRect &&
        Math.abs(current.cropRect.x - newRect.x) < 0.0005 &&
        Math.abs(current.cropRect.y - newRect.y) < 0.0005 &&
        Math.abs(current.cropRect.width - newRect.width) < 0.0005 &&
        Math.abs(current.cropRect.height - newRect.height) < 0.0005
      ) {
        return current
      }
      return { ...current, cropRect: newRect }
    })
  }, [])

  const endOriginalPreview = useCallback(() => {
    if (originalPressTimerRef.current !== null) {
      window.clearTimeout(originalPressTimerRef.current)
      originalPressTimerRef.current = null
    }
    originalPressStartRef.current = null
    setShowOriginalWhilePressed(false)
  }, [])

  useEffect(() => () => {
    if (originalPressTimerRef.current !== null) window.clearTimeout(originalPressTimerRef.current)
  }, [])

  if (previousEditorTarget.open !== open || previousEditorTarget.activeIndex !== activeIndex) {
    setPreviousEditorTarget({ open, activeIndex })
    if (open) {
      setEdits(emptyEdits)
      setActiveTool(null)
      setDownloadError('')
      setShowOriginalWhilePressed(false)
      setPreviewReadyFor(null)
      setSaveDialogOpen(false)
      setConfirmDiscardOpen(false)
    }
  }

  const requestClose = useCallback(() => {
    if (hasPreviewEdits(edits) && !isSaving) {
      void haptic('medium')
      setConfirmDiscardOpen(true)
      return
    }
    onClose()
  }, [edits, isSaving, onClose])

  useEffect(() => {
    cropTransformRef.current = { scale: 1, position: { x: 0, y: 0 } }
  }, [activeIndex, open])

  // Align horizontal scroll position when opened
  useEffect(() => {
    if (!open) return
    const safeIdx = Math.max(0, Math.min(items.length - 1, initialIndex))
    const timer = setTimeout(() => {
      if (trackRef.current && trackRef.current.clientWidth > 0) {
        trackRef.current.scrollLeft = safeIdx * trackRef.current.clientWidth
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [open, initialIndex, items.length])

  // Lock body scroll when preview sheet is open
  useEffect(() => {
    if (!open) return

    document.body.classList.add('has-style-modal-open')
    const prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.classList.remove('has-style-modal-open')
      document.documentElement.style.overflow = prevOverflow
    }
  }, [open])

  // Keyboard navigation (Escape, ArrowLeft, ArrowRight)
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (saveDialogOpen || confirmDiscardOpen) return
      const target = event.target
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight')
        && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return
      if (event.key === 'Escape') {
        requestClose()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        const next = Math.max(0, activeIndex - 1)
        setActiveIndex(next)
        if (trackRef.current) {
          trackRef.current.scrollTo({
            left: next * trackRef.current.clientWidth,
            behavior: 'smooth',
          })
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        const next = Math.min(items.length - 1, activeIndex + 1)
        setActiveIndex(next)
        if (trackRef.current) {
          trackRef.current.scrollTo({
            left: next * trackRef.current.clientWidth,
            behavior: 'smooth',
          })
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, activeIndex, items.length, requestClose, saveDialogOpen, confirmDiscardOpen])

  // Track horizontal scrolling across all items
  const handleTrackScroll = () => {
    if (scrollFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollFrameRef.current)
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const track = trackRef.current
      if (!track || track.clientWidth <= 0) return
      const next = Math.max(
        0,
        Math.min(items.length - 1, Math.round(track.scrollLeft / track.clientWidth)),
      )
      setActiveIndex((curr) => (curr === next ? curr : next))
    })
  }

  const rafPreviewRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open) {
      releaseStudioImagePreviewSource()
      return
    }
    const item = items[activeIndex]
    if (!item) return
    const generation = ++previewGenerationRef.current
    const hasEdits = hasPreviewEdits(edits)
    if (!hasEdits) {
      return
    }
    const canvas = previewCanvasRef.current
    if (!canvas) return
    const isCurrent = () => previewGenerationRef.current === generation

    // Cancel any pending RAF to avoid stacking frames
    if (rafPreviewRef.current !== null) {
      window.cancelAnimationFrame(rafPreviewRef.current)
      rafPreviewRef.current = null
    }

    // Schedule via RAF for smooth 60fps slider updates
    rafPreviewRef.current = window.requestAnimationFrame(() => {
      rafPreviewRef.current = null
      if (!isCurrent()) return
      void renderStudioImageToCanvas(item, edits, canvas, isCurrent).then(() => {
        if (isCurrent()) {
          setPreviewReadyFor(item.id)
          setDownloadError('')
        }
      }).catch((error) => {
          if (isCurrent()) {
            console.error('[Voxnela Preview] Görsel düzenleme önizlemesi oluşturulamadı', error)
            setDownloadError(error instanceof Error ? error.message : (locale === 'tr' ? 'Görsel düzenleme bu cihazda başlatılamadı.' : 'Image editing could not start on this device.'))
          }
      })
    })

    return () => {
      previewGenerationRef.current += 1
      if (rafPreviewRef.current !== null) {
        window.cancelAnimationFrame(rafPreviewRef.current)
        rafPreviewRef.current = null
      }
    }
  }, [activeIndex, edits, items, locale, open])

  const activeItem = items[activeIndex] ?? items[0]
  const cropPreviewKey = `${activeItem?.id ?? ''}:${activeItem?.aspectRatio ?? ''}:${edits.cropRatio}:${edits.rotation}`
  const currentCropPreviewSize = cropPreviewSize?.key === cropPreviewKey ? cropPreviewSize : null

  useEffect(() => {
    if (!open || activeTool !== 'filters' || !activeItem) return
    let cancelled = false
    void getStudioLookThumbnails(activeItem, activeFilterCategory).then((urls) => {
      if (!cancelled) setLookThumbnails((current) => ({
        itemId: activeItem.id,
        urls: { ...(current?.itemId === activeItem.id ? current.urls : {}), ...urls },
      }))
    }).catch((error) => {
      console.error('[Voxnela Preview] Filtre örnekleri hazırlanamadı', error)
    })
    return () => { cancelled = true }
  }, [activeItem, activeTool, activeFilterCategory, open])

  useEffect(() => {
    if (!open || activeTool !== 'beauty' || !activeItem) {
      return
    }
    let cancelled = false
    void Promise.resolve().then(async () => {
      if (!cancelled) setFaceAnalysisState('loading')
      const faces = await analyzeStudioImageFaces(activeItem)
      if (!cancelled) setFaceAnalysisState(faces.length ? 'ready' : 'none')
    }).catch((error) => {
      console.error('[Voxnela Retouch] Yüz analizi başlatılamadı', error)
      if (!cancelled) setFaceAnalysisState('error')
    })
    return () => { cancelled = true }
  }, [activeItem, activeTool, open])

  useEffect(() => {
    if (!open || !activeItem) {
      return
    }
    const slide = previewSlideRef.current
    if (!slide) {
      setCropPreviewSize(null)
      return
    }
    const [sourceRatioWidth, sourceRatioHeight] = (activeItem.aspectRatio || '').split(':').map(Number)
    const sourceRatio = sourceRatioWidth && sourceRatioHeight ? sourceRatioWidth / sourceRatioHeight : 0
    const [ratioWidth, ratioHeight] = edits.cropRatio && edits.cropRatio !== 'free'
      ? edits.cropRatio.split(':').map(Number)
      : sourceRatio
        ? [sourceRatio, 1]
        : []
    if (!ratioWidth || !ratioHeight) {
      return
    }
    const updateSize = () => {
      const maxWidth = Math.max(1, slide.clientWidth - 16)
      const maxHeight = Math.max(1, slide.clientHeight - 16)
      let ratio = ratioWidth / ratioHeight
      if (Math.abs(edits.rotation % 180) === 90) ratio = 1 / ratio
      const width = Math.min(maxWidth, maxHeight * ratio)
      const height = width / ratio
      setCropPreviewSize((current) => current?.key === cropPreviewKey && current.width === width && current.height === height
        ? current
        : { width, height, key: cropPreviewKey })
    }
    updateSize()
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateSize)
      observer.observe(slide)
      return () => observer.disconnect()
    }
  }, [activeItem, cropPreviewKey, edits.cropRatio, edits.rotation, open])

  if (!open || !activeItem) return null

  const project = activeItem.project
  const styleTitle = project
    ? getProjectStyleTitle(project, copy.defaultStyle, locale)
    : activeItem.title && activeItem.title.trim()
      ? activeItem.title
      : copy.styles

  const activeVer = project
    ? (project.versions.find((v) => v.version === (activeItem.version ?? project.currentVersion))
        ?? project.versions[project.versions.length - 1])
    : null

  const formattedDate = formatProductionDate(
    project?.createdAtMs || activeVer?.createdAtMs || activeItem.createdAtMs,
    locale,
  )

  const localizedModeLabels = {
    style_transfer: copy.modeStyleTransfer,
    virtual_tryon: copy.modeVirtualTryOn,
    magazine_cover: copy.modeMagazineCover,
    poster_studio: copy.modePosterStudio,
    commercial: copy.modeCommercial,
    time_warp: copy.modeTimeWarp,
    outpaint_relight: copy.modeOutpaintRelight,
    infographic: copy.modeInfographic,
    portrait_retouch: copy.modePortraitRetouch,
    artistic_collage: copy.modeArtisticCollage,
  }
  const categoryLabel = project
    ? (project.versions[0]?.source === 'upload' ? copy.uploadedImage : localizedModeLabels[project.mode] ?? '')
    : (activeItem.subtitle || copy.styles)

  const headerSubtitle = categoryLabel && formattedDate
    ? `${categoryLabel} · ${formattedDate}`
    : categoryLabel || formattedDate

  const handleDownload = async () => {
    if (isDownloading) return
    void haptic('selection')
    setDownloadError('')
    setIsDownloading(true)
    try {
      await downloadStudioImage(activeItem, styleTitle, {
        ...edits,
        cropZoom: activeTool === 'crop' ? cropTransformRef.current.scale : 1,
        cropPanX: activeTool === 'crop' ? cropTransformRef.current.position.x : 0,
        cropPanY: activeTool === 'crop' ? cropTransformRef.current.position.y : 0,
        cropFrameWidth: currentCropPreviewSize?.width || 1,
        cropFrameHeight: currentCropPreviewSize?.height || 1,
      })
      void haptic('success')
    } catch (error) {
      console.error('[Voxnela Share] Görsel paylaşılamadı', error)
      setDownloadError(error instanceof Error ? error.message : 'Paylaşım başarısız oldu. Tekrar dene.')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleSaveToGallery = async () => {
    if (isSavingToGallery) return
    void haptic('selection')
    setDownloadError('')
    setIsSavingToGallery(true)
    try {
      await saveStudioImageToGallery(activeItem, styleTitle, {
        ...edits,
        cropZoom: activeTool === 'crop' ? cropTransformRef.current.scale : 1,
        cropPanX: activeTool === 'crop' ? cropTransformRef.current.position.x : 0,
        cropPanY: activeTool === 'crop' ? cropTransformRef.current.position.y : 0,
        cropFrameWidth: currentCropPreviewSize?.width || 1,
        cropFrameHeight: currentCropPreviewSize?.height || 1,
      })
      void haptic('success')
    } catch (error) {
      console.error('[Voxnela Gallery] Görsel galeriye kaydedilemedi', error)
      setDownloadError(error instanceof Error ? error.message : 'Galeriye kaydedilemedi. Tekrar dene.')
    } finally {
      setIsSavingToGallery(false)
    }
  }



  const handleSave = async (mode: 'overwrite' | 'copy') => {
    if (isSaving || !activeItem.projectId || !activeItem.version) return
    setIsSaving(true)
    setSavingMode(mode)
    setDownloadError('')
    try {
      const saveEdits: StudioImageEdits = {
        ...edits,
        cropZoom: activeTool === 'crop' ? cropTransformRef.current.scale : 1,
        cropPanX: activeTool === 'crop' ? cropTransformRef.current.position.x : 0,
        cropPanY: activeTool === 'crop' ? cropTransformRef.current.position.y : 0,
        cropFrameWidth: currentCropPreviewSize?.width || 1,
        cropFrameHeight: currentCropPreviewSize?.height || 1,
      }
      const blob = await renderStudioImageBlob(activeItem, saveEdits)
      await saveStudioImageEdits(activeItem.projectId, activeItem.version, mode, blob)
      setSaveDialogOpen(false)
      void haptic('success')
      onClose()
    } catch (error) {
      console.error('[Voxnela Save] Görsel kaydedilemedi', error)
      setSaveDialogOpen(false)
      setDownloadError(error instanceof Error ? error.message : 'Görsel kaydedilemedi. Tekrar dene.')
    } finally {
      setIsSaving(false)
      setSavingMode(null)
    }
  }

  const localizedEditorLabels = editorLabels[locale] ?? editorLabels.en!
  const shouldShowProcessedPreview = hasPreviewEdits(edits) && previewReadyFor === activeItem.id && !showOriginalWhilePressed && activeTool !== 'crop'
  const updateEdit = <K extends keyof StudioImageEdits>(key: K, value: StudioImageEdits[K]) => {
    setEdits((current) => ({ ...current, [key]: value }))
  }
  const updateBeauty = (key: keyof StudioImageEdits['beauty'], value: number) => {
    setEdits((current) => ({
      ...current,
      beautyEnabled: true,
      beauty: { ...current.beauty, [key]: value },
      beautyTouched: { ...current.beautyTouched, [key]: true },
    }))
  }
  const toolItems: EditorTool[] = ['beauty', 'filters', 'light', 'contrast', 'color', 'temperature', 'tint', 'detail', 'effects', 'vignette', 'crop', 'rotate', 'flip', 'reset']
  const activeToolLabel = activeTool ? localizedEditorLabels[activeTool] : ''
  const adjustmentConfigs = activeTool === 'light'
    ? [
      { key: 'brightness' as const, min: -100, max: 100, value: edits.brightness, label: locale === 'tr' ? 'Pozlama' : 'Exposure' },
      { key: 'highlights' as const, min: -100, max: 100, value: edits.highlights, label: locale === 'tr' ? 'Açık alanlar' : 'Highlights' },
      { key: 'shadows' as const, min: -100, max: 100, value: edits.shadows, label: locale === 'tr' ? 'Gölgeler' : 'Shadows' },
    ]
    : activeTool === 'contrast'
      ? [{ key: 'contrast' as const, min: -60, max: 80, value: edits.contrast, label: localizedEditorLabels.contrast }]
      : activeTool === 'color'
        ? [{ key: 'saturation' as const, min: -100, max: 100, value: edits.saturation, label: locale === 'tr' ? 'Doygunluk' : 'Saturation' }]
        : activeTool === 'temperature'
          ? [{ key: 'warmth' as const, min: -100, max: 100, value: edits.warmth, label: localizedEditorLabels.temperature }]
          : activeTool === 'tint'
            ? [{ key: 'tint' as const, min: -100, max: 100, value: edits.tint, label: locale === 'tr' ? 'Yeşil / Macenta' : 'Green / Magenta' }]
            : activeTool === 'detail'
              ? [{ key: 'sharpness' as const, min: 0, max: 100, value: edits.sharpness, label: locale === 'tr' ? 'Netlik' : 'Sharpness' }]
              : activeTool === 'effects'
                ? [
                  { key: 'fade' as const, min: 0, max: 100, value: edits.fade, label: locale === 'tr' ? 'Solukluk' : 'Fade' },
                  { key: 'grain' as const, min: 0, max: 100, value: edits.grain, label: locale === 'tr' ? 'Gren' : 'Grain' },
                ]
                : activeTool === 'vignette'
                  ? [
                    { key: 'vignette' as const, min: 0, max: 100, value: edits.vignette, label: localizedEditorLabels.vignette },
                    { key: 'vignetteFeather' as const, min: 0, max: 100, value: edits.vignetteFeather, label: locale === 'tr' ? 'Geçiş' : 'Feather' },
                  ]
                  : null

  const beautyCategoryLabels: Record<BeautyCategory, string> = locale === 'tr'
    ? { auto: 'Otomatik', skin: 'Yüz', blemish: 'Leke', underEye: 'Göz altı', eyes: 'Göz', teeth: 'Diş', lips: 'Dudak', faceLight: 'Yüz ışığı', shine: 'Parlama', glow: 'Glow' }
    : { auto: 'Auto', skin: 'Face', blemish: 'Blemish', underEye: 'Under-eye', eyes: 'Eyes', teeth: 'Teeth', lips: 'Lips', faceLight: 'Face light', shine: 'Shine', glow: 'Glow' }
  const beautyCategories: BeautyCategory[] = ['auto', 'skin', 'blemish', 'underEye', 'eyes', 'teeth', 'lips', 'faceLight', 'shine', 'glow']
  const beautySliders: Partial<Record<BeautyCategory, Array<{ key: keyof StudioImageEdits['beauty']; label: string; min?: number; max?: number }>>> = {
    skin: [
      { key: 'skinSmooth', label: locale === 'tr' ? 'Pürüzsüzlük' : 'Smoothness' },
      { key: 'skinTexture', label: locale === 'tr' ? 'Doku' : 'Texture', min: -100 },
      { key: 'skinTone', label: locale === 'tr' ? 'Ton eşitle' : 'Even tone' },
      { key: 'skinRedness', label: locale === 'tr' ? 'Kızarıklık' : 'Redness' },
      { key: 'skinVitality', label: locale === 'tr' ? 'Canlılık' : 'Vitality' },
    ],
    blemish: [{ key: 'blemish', label: locale === 'tr' ? 'Otomatik leke azaltma' : 'Automatic blemish reduction' }],
    underEye: [{ key: 'underEye', label: locale === 'tr' ? 'Göz altı' : 'Under-eye' }],
    eyes: [
      { key: 'eyeBrightness', label: locale === 'tr' ? 'Parlaklık' : 'Brightness' },
      { key: 'eyeWhitening', label: locale === 'tr' ? 'Beyazlık' : 'Whitening' },
      { key: 'eyeClarity', label: locale === 'tr' ? 'Netlik' : 'Clarity' },
      { key: 'eyeVitality', label: locale === 'tr' ? 'Canlılık' : 'Vitality' },
    ],
    teeth: [{ key: 'teethWhitening', label: locale === 'tr' ? 'Beyazlat' : 'Whiten', max: 55 }],
    lips: [
      { key: 'lipVitality', label: locale === 'tr' ? 'Canlılık' : 'Vitality' },
      { key: 'lipBrightness', label: locale === 'tr' ? 'Parlaklık' : 'Brightness' },
    ],
    faceLight: [
      { key: 'faceLight', label: locale === 'tr' ? 'Aydınlat' : 'Brighten' },
      { key: 'faceShadows', label: locale === 'tr' ? 'Gölgeler' : 'Shadows' },
      { key: 'faceHighlights', label: locale === 'tr' ? 'Parlak alanlar' : 'Highlights', min: -100 },
      { key: 'faceTemperature', label: locale === 'tr' ? 'Sıcaklık' : 'Warmth', min: -100 },
    ],
    shine: [{ key: 'shineReduction', label: locale === 'tr' ? 'Yağlı parlamayı azalt' : 'Reduce oily shine' }],
    glow: [
      { key: 'glow', label: locale === 'tr' ? 'Yoğunluk' : 'Intensity' },
      { key: 'glowSpread', label: locale === 'tr' ? 'Yayılım' : 'Spread' },
    ],
  }

  return <>
  {createPortal(
    <div
      className="visual-style-modal-root float-island-sheet-root fortale-floatisland-sheet-root"
      role="presentation"
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) requestClose()
      }}
    >
      <section
        className="visual-style-modal-sheet float-island-sheet fortale-floatisland-sheet-panel fortale-sheet-surface"
        role="dialog"
        aria-modal="true"
        aria-label={copy.imageDetails}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <button
          className="float-island-sheet-handle fortale-sheet-handle"
          type="button"
          aria-label={text('dragToClose')}
          tabIndex={-1}
          onClick={requestClose}
        />

        {/* Header: Başlıkta sadece stil adı, alt satırda kategori ve üretim tarihi */}
        <header className="float-island-sheet-header fortale-sheet-header">
          <div className="float-island-sheet-heading">
            <span>
              <strong>{styleTitle}</strong>
              {headerSubtitle && <small>{headerSubtitle}</small>}
            </span>
          </div>
          <div className="float-island-sheet-actions">
            <button
              type="button"
              className="studio-image-detail-header-save"
              onClick={() => { void handleSaveToGallery() }}
              disabled={isSavingToGallery}
              aria-busy={isSavingToGallery}
              data-saving={isSavingToGallery ? 'true' : undefined}
              aria-label={locale === 'tr' ? 'Galeriye kaydet' : 'Save to gallery'}
            >
              {isSavingToGallery ? <VoxnelaMark animated /> : <Save size={16} strokeWidth={2.2} />}
              <span>{locale === 'tr' ? 'Kaydet' : 'Save'}</span>
            </button>
            <button
              type="button"
              className="studio-image-detail-header-download"
              onClick={() => { void handleDownload() }}
              aria-label={copy.downloadImage}
              title={copy.downloadImage}
              aria-busy={isDownloading}
              disabled={isDownloading}
            >
              {isDownloading
                ? <VoxnelaMark animated />
                : <Download size={18} strokeWidth={2.3} />}
            </button>
            <button
              type="button"
              className="app-close-button float-island-sheet-close"
              onClick={requestClose}
              aria-label={text('close')}
            >
              <X size={19} strokeWidth={2.4} />
            </button>
          </div>
        </header>

        {/* Body: Sağa-sola kaydırılabilir track (aynı stil sheeti gibi) + Pure Native Zoom */}
        <div className="visual-style-modal-body">
          <div
            ref={trackRef}
            className="visual-style-carousel-track"
            onScroll={handleTrackScroll}
          >
            {items.map((item, idx) => (
              <article
                key={item.id}
                ref={idx === activeIndex ? previewSlideRef : null}
                className="visual-style-slide studio-image-detail-slide"
              >
                <div
                  className="studio-image-detail-frame"
                  onPointerDown={(event) => {
                    if (idx !== activeIndex || !hasPreviewEdits(edits) || manualBlemishMode || activeTool === 'crop') return
                    if (event.pointerType === 'mouse' && event.button !== 0) return
                    if (event.pointerType === 'touch' && !event.isPrimary) {
                      endOriginalPreview()
                      return
                    }
                    if (!(event.target as HTMLElement).closest('img')) return
                    originalPressStartRef.current = { x: event.clientX, y: event.clientY }
                    if (originalPressTimerRef.current !== null) window.clearTimeout(originalPressTimerRef.current)
                    originalPressTimerRef.current = window.setTimeout(() => {
                      originalPressTimerRef.current = null
                      setShowOriginalWhilePressed(true)
                    }, 400)
                  }}
                  onPointerMove={(event) => {
                    const start = originalPressStartRef.current
                    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 14) endOriginalPreview()
                  }}
                  onPointerUp={endOriginalPreview}
                  onPointerCancel={endOriginalPreview}
                  onPointerLeave={endOriginalPreview}
                  onContextMenu={(event) => event.preventDefault()}
                  onClick={(event) => {
                    if (!manualBlemishMode || idx !== activeIndex || faceAnalysisState !== 'ready') return
                    const image = (event.target as HTMLElement).closest('img')
                    if (!image) return
                    const rect = image.getBoundingClientRect()
                    const x = (event.clientX - rect.left) / rect.width
                    const y = (event.clientY - rect.top) / rect.height
                    if (x < 0 || x > 1 || y < 0 || y > 1) return
                    setEdits((current) => ({
                      ...current,
                      blemishSpots: [...current.blemishSpots, {
                        x,
                        y,
                        radius: current.blemishBrushSize / Math.min(rect.width, rect.height),
                      }],
                    }))
                    void haptic('selection')
                  }}
                  data-retouching={manualBlemishMode && idx === activeIndex ? 'true' : undefined}
                  ref={idx === activeIndex ? activeFrameRef : null}
                  style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
                >
                <ZoomableImage
                  src={item.imageUrl}
                  alt={item.title || styleTitle}
                  isActive={activeIndex === idx}
                  resetZoomOnSourceChange={false}
                  overlayCanvasRef={idx === activeIndex ? previewCanvasRef : undefined}
                  showOverlay={idx === activeIndex && shouldShowProcessedPreview}
                  className="studio-image-detail-native-preview"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    display: activeTool === 'crop' && idx === activeIndex ? 'none' : undefined,
                  }}
                  imgStyle={{
                    boxShadow: 'none',
                    filter: 'none',
                    objectFit: 'contain',
                    width: 'auto',
                    height: 'auto',
                    rotate: shouldShowProcessedPreview && idx === activeIndex ? '0deg' : `${edits.rotation}deg`,
                    scale: shouldShowProcessedPreview && idx === activeIndex ? '1 1' : `${edits.flipHorizontal ? -1 : 1} ${edits.flipVertical ? -1 : 1}`,
                  }}
                  onTransformChange={idx === activeIndex ? handleZoomTransformChange : undefined}
                />
                {activeTool === 'crop' && idx === activeIndex && (
                  <StudioCropOverlay
                    imageUrl={item.imageUrl}
                    cropRatio={edits.cropRatio || 'free'}
                    cropRect={edits.cropRect}
                    onChange={handleCropChange}
                    containerRef={activeFrameRef}
                    zoomTransform={zoomTransform}
                    rotation={edits.rotation}
                    flipHorizontal={edits.flipHorizontal}
                    flipVertical={edits.flipVertical}
                  />
                )}
                </div>
              </article>
            ))}
          </div>

          <div className="studio-image-editor-dock">
            {activeTool && (activeTool !== 'beauty' || activeBeautyCategory) && (
              <div className="studio-image-editor-panel" aria-label={activeToolLabel}>
                {activeTool === 'beauty' && activeBeautyCategory && (
                  <div className="studio-image-beauty-panel-content">
                    {activeBeautyCategory === 'auto' ? (
                      <div className="studio-image-beauty-controls">
                        <div className="studio-image-beauty-presets" role="group" aria-label={locale === 'tr' ? 'Rötuş tarzı' : 'Retouch style'}>
                          {(['natural', 'clean', 'bright', 'soft', 'glam'] as const).map((preset) => {
                            const labels = locale === 'tr'
                              ? { natural: 'Doğal', clean: 'Temiz', bright: 'Parlak', soft: 'Soft', glam: 'Glam' }
                              : { natural: 'Natural', clean: 'Clean', bright: 'Bright', soft: 'Soft', glam: 'Glam' }
                            return (
                              <button
                                key={preset}
                                type="button"
                                className={`studio-image-beauty-preset${edits.beautyPreset === preset ? ' is-active' : ''}`}
                                aria-pressed={edits.beautyPreset === preset}
                                onClick={() => setEdits((current) => ({ ...current, beautyEnabled: true, beautyPreset: preset }))}
                              >
                                {labels[preset]}
                              </button>
                            )
                          })}
                          <button
                            type="button"
                            className="studio-image-beauty-preset studio-image-beauty-reset"
                            onClick={() => {
                              void haptic('medium')
                              setEdits((current) => ({
                                ...current,
                                beautyEnabled: false,
                                beautyPreset: 'natural',
                                beautyIntensity: 70,
                                beautyTouched: {},
                                blemishSpots: [],
                                beauty: { ...emptyEdits.beauty },
                              }))
                              setManualBlemishMode(false)
                            }}
                          >
                            {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                          </button>
                        </div>
                        <div className="studio-image-filter-intensity">
                          <span>{locale === 'tr' ? 'Yoğunluk' : 'Intensity'}</span>
                          <input type="range" min="0" max="100" value={edits.beautyIntensity} aria-label={locale === 'tr' ? 'Rötuş yoğunluğu' : 'Retouch intensity'} onChange={(event) => updateEdit('beautyIntensity', Number(event.currentTarget.value))} />
                          <output>{edits.beautyIntensity}</output>
                          <button type="button" className="studio-image-adjustment-reset" onClick={() => updateEdit('beautyIntensity', 70)}>
                            {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="studio-image-beauty-controls">
                        <div className="studio-image-adjustments-stack">
                          {(beautySliders[activeBeautyCategory] ?? []).map((config) => (
                            <label className="studio-image-adjustment-control" key={config.key}>
                              <span>{config.label}</span>
                              {(() => {
                                const value = edits.beautyTouched?.[config.key]
                                  ? (edits.beauty?.[config.key] ?? 0)
                                  : edits.beautyEnabled
                                    ? (STUDIO_BEAUTY_PRESETS[edits.beautyPreset]?.[config.key] ?? edits.beauty?.[config.key] ?? 0)
                                    : (edits.beauty?.[config.key] ?? 0)
                                return <>
                              <input
                                type="range"
                                min={config.min ?? 0}
                                max={config.max ?? 100}
                                value={value}
                                onChange={(event) => updateBeauty(config.key, Number(event.currentTarget.value))}
                              />
                              <output>{value}</output>
                              <button type="button" className="studio-image-adjustment-reset" onClick={() => updateBeauty(config.key, 0)}>
                                {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                              </button>
                                </>
                              })()}
                            </label>
                          ))}
                        </div>
                        {activeBeautyCategory === 'blemish' && (
                          <>
                            <div className="studio-image-transform-options studio-image-blemish-actions">
                              <button type="button" aria-pressed={manualBlemishMode} disabled={faceAnalysisState !== 'ready'} onClick={() => setManualBlemishMode((current) => !current)}>
                                <ScanFace size={16} />{manualBlemishMode ? (locale === 'tr' ? 'Dokunma modunu kapat' : 'Finish touch-up') : (locale === 'tr' ? 'Dokunarak sil' : 'Tap to remove')}
                              </button>
                              <button type="button" disabled={!edits.blemishSpots.length} onClick={() => setEdits((current) => ({ ...current, blemishSpots: current.blemishSpots.slice(0, -1) }))}>
                                <RotateCcw size={16} />{locale === 'tr' ? 'Geri al' : 'Undo'}
                              </button>
                            </div>
                            {manualBlemishMode && (
                              <label className="studio-image-filter-intensity">
                                <span>{locale === 'tr' ? 'Fırça boyutu' : 'Brush size'}</span>
                                <input type="range" min="8" max="40" value={edits.blemishBrushSize} onChange={(event) => updateEdit('blemishBrushSize', Number(event.currentTarget.value))} />
                                <output>{edits.blemishBrushSize}px</output>
                              </label>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {faceAnalysisState !== 'ready' && (
                      <p className="studio-image-beauty-status" role={faceAnalysisState === 'error' ? 'alert' : 'status'}>
                        {faceAnalysisState === 'loading' && (locale === 'tr' ? 'Yüz alanları hazırlanıyor… İlk kullanım biraz sürebilir.' : 'Preparing face regions… First use may take a moment.')}
                        {faceAnalysisState === 'none' && (locale === 'tr' ? 'Bu görselde rötuş yapılabilecek bir yüz algılanamadı.' : 'No face was detected for retouching in this image.')}
                        {faceAnalysisState === 'error' && (locale === 'tr' ? 'Yüz analizi başlatılamadı. Bağlantını kontrol edip yeniden dene.' : 'Face analysis could not start. Check your connection and retry.')}
                      </p>
                    )}
                  </div>
                )}
                {activeTool === 'filters' && (
                  <div className="studio-image-filter-panel-content">
                    <div className="studio-image-filter-categories" role="tablist" aria-label={locale === 'tr' ? 'Filtre kategorileri' : 'Filter categories'}>
                      {STUDIO_FILTER_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          role="tab"
                          className={`studio-image-filter-category-tab${activeFilterCategory === cat.id ? ' is-active' : ''}`}
                          aria-selected={activeFilterCategory === cat.id}
                          onClick={() => setActiveFilterCategory(cat.id)}
                        >
                          {locale === 'tr' ? cat.label : cat.englishLabel}
                        </button>
                      ))}
                    </div>
                    <div className="studio-image-filter-options">
                    {STUDIO_LOOKS.filter((l) => l.id === 'original' || l.category === activeFilterCategory).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`studio-image-filter-option${edits.filter === option.id ? ' is-active' : ''}`}
                        aria-pressed={edits.filter === option.id}
                        onClick={() => updateEdit('filter', option.id)}
                      >
                        <img src={lookThumbnails?.itemId === activeItem.id ? (lookThumbnails.urls?.[option.id] ?? activeItem.imageUrl) : activeItem.imageUrl} alt="" />
                        <span>{locale === 'tr' ? option.label : option.englishLabel}</span>
                      </button>
                    ))}
                    </div>
                    <div className="studio-image-filter-intensity">
                      <span>{locale === 'tr' ? 'Yoğunluk' : 'Intensity'}</span>
                      <input type="range" min="0" max="100" step="1" value={edits.filterIntensity} aria-label={locale === 'tr' ? 'Filtre yoğunluğu' : 'Filter intensity'} onChange={(event) => updateEdit('filterIntensity', Number(event.currentTarget.value))} />
                      <output>{edits.filterIntensity}</output>
                      <button type="button" className="studio-image-adjustment-reset" onClick={() => updateEdit('filterIntensity', 100)}>
                        {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                      </button>
                    </div>
                  </div>
                )}
                {adjustmentConfigs && (
                  <div className="studio-image-adjustments-stack">
                    {adjustmentConfigs.map((config) => (
                      <label className="studio-image-adjustment-control" key={config.key}>
                        <span>{config.label}</span>
                        <input type="range" min={config.min} max={config.max} step="1" value={config.value} onChange={(event) => updateEdit(config.key, Number(event.currentTarget.value))} />
                        <output>{config.value}</output>
                        <button type="button" className="studio-image-adjustment-reset" onClick={() => updateEdit(config.key, config.key === 'vignetteFeather' ? 60 : 0)}>
                          {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                        </button>
                      </label>
                    ))}
                  </div>
                )}
                {activeTool === 'crop' && (
                  <div className="studio-image-crop-options">
                    <div className="studio-image-ratio-options">
                      {cropOptions.map((option) => (
                        <button
                          key={option.value || 'free'}
                          type="button"
                          className={`studio-image-ratio-option${(edits.cropRatio === option.value || (option.value === '' && (!edits.cropRatio || edits.cropRatio === 'free'))) ? ' is-active' : ''}`}
                          aria-pressed={edits.cropRatio === option.value || (option.value === '' && (!edits.cropRatio || edits.cropRatio === 'free'))}
                          onClick={() => {
                            void haptic('selection')
                            updateEdit('cropRatio', option.value || 'free')
                          }}
                        >
                          {option.value ? option.label : locale === 'tr' ? option.label : 'Free'}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="studio-image-adjustment-reset"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => {
                          void haptic('selection')
                          setEdits((current) => ({
                            ...current,
                            cropRect: undefined,
                            cropRatio: 'free',
                          }))
                        }}
                      >
                        {locale === 'tr' ? 'Sıfırla' : 'Reset'}
                      </button>
                    </div>
                  </div>
                )}
                {activeTool === 'rotate' && (
                  <div className="studio-image-transform-options">
                    <button type="button" onClick={() => updateEdit('rotation', (edits.rotation + 270) % 360)}><RotateCcw size={17} />{locale === 'tr' ? 'Sola döndür' : 'Rotate left'}</button>
                    <button type="button" onClick={() => updateEdit('rotation', (edits.rotation + 90) % 360)}><RotateCw size={17} />{locale === 'tr' ? 'Sağa döndür' : 'Rotate right'}</button>
                  </div>
                )}
                {activeTool === 'flip' && (
                  <div className="studio-image-transform-options">
                    <button type="button" aria-pressed={edits.flipHorizontal} onClick={() => updateEdit('flipHorizontal', !edits.flipHorizontal)}>
                      <FlipHorizontal2 size={17} />{locale === 'tr' ? 'Yatay yansıt' : 'Flip horizontally'}
                    </button>
                    <button type="button" aria-pressed={edits.flipVertical} onClick={() => updateEdit('flipVertical', !edits.flipVertical)}>
                      <FlipVertical2 size={17} />{locale === 'tr' ? 'Dikey yansıt' : 'Flip vertically'}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className={`studio-image-editor-tools${activeTool === 'beauty' ? ' is-beauty-mode' : ''}`} role="toolbar" aria-label={locale === 'tr' ? 'Görsel araçları' : 'Image tools'}>
              {toolItems.filter((tool) => activeTool !== 'beauty' || tool === 'beauty').map((tool) => {
                const Icon = editorToolIcons[tool]
                return (
                  <Fragment key={tool}>
                  <button
                    type="button"
                    className={`studio-image-editor-tool${tool === 'beauty' ? ' studio-image-editor-tool--beauty' : ''}${activeTool === tool ? ' is-active' : ''}`}
                    aria-label={localizedEditorLabels[tool]}
                    aria-pressed={activeTool === tool}
                    onClick={() => {
                      if (tool === 'reset') {
                        setEdits(emptyEdits)
                        setActiveTool(null)
                        setActiveBeautyCategory(null)
                        setManualBlemishMode(false)
                      } else {
                        if (tool === 'crop' && !edits.cropRatio) updateEdit('cropRatio', 'free')
                        if (tool === 'beauty') {
                          setActiveBeautyCategory(null)
                          setManualBlemishMode(false)
                        } else {
                          setActiveBeautyCategory(null)
                          setFaceAnalysisState('idle')
                          setManualBlemishMode(false)
                        }
                        setActiveTool((current) => current === tool ? null : tool)
                      }
                    }}
                  >
                    <Icon size={17} strokeWidth={2.1} />
                    <span>{localizedEditorLabels[tool]}</span>
                  </button>
                  {tool === 'beauty' && activeTool === 'beauty' && <div className="studio-image-beauty-subtools-scroll">
                    {beautyCategories.map((category) => {
                      const BeautyIcon = beautyToolIcons[category]
                      return (
                        <button
                          key={category}
                          type="button"
                          className={`studio-image-editor-tool studio-image-editor-subtool${activeBeautyCategory === category ? ' is-active' : ''}`}
                          aria-label={beautyCategoryLabels[category]}
                          aria-pressed={activeBeautyCategory === category}
                          onClick={() => {
                            setActiveBeautyCategory(category)
                            setManualBlemishMode(false)
                            if (category === 'auto') setEdits((current) => ({ ...current, beautyEnabled: true }))
                          }}
                        >
                          <BeautyIcon size={15} strokeWidth={2.1} />
                          <span>{beautyCategoryLabels[category]}</span>
                        </button>
                      )
                    })}
                    <button
                      type="button"
                      className="studio-image-editor-tool studio-image-editor-subtool studio-image-editor-subtool--reset"
                      aria-label={locale === 'tr' ? 'Güzelleştirmeyi sıfırla' : 'Reset retouching'}
                      onClick={() => {
                        void haptic('medium')
                        setEdits((current) => ({
                          ...current,
                          beautyEnabled: false,
                          beautyPreset: 'natural',
                          beautyIntensity: 70,
                          beautyTouched: {},
                          blemishSpots: [],
                          beauty: { ...emptyEdits.beauty },
                        }))
                        setManualBlemishMode(false)
                      }}
                    >
                      <RotateCcw size={15} strokeWidth={2.1} />
                      <span>{locale === 'tr' ? 'Sıfırla' : 'Reset'}</span>
                    </button>
                  </div>}
                  </Fragment>
                )
              })}
            </div>
            {downloadError && <p className="studio-image-download-error" role="alert">{downloadError}</p>}
          </div>
        </div>
      </section>
    </div>,
    document.body,
  )}
  <VoxnelaAlertModal
    open={saveDialogOpen}
    title={locale === 'tr' ? 'Görseli kaydet' : 'Save image'}
    message={locale === 'tr'
      ? 'Değişiklikleri mevcut görselin üzerine kaydet veya orijinali koruyarak yeni görsel oluştur.'
      : 'Replace this image with your edits, or keep the original and create a new image.'}
    primaryLabel={locale === 'tr' ? 'Farklı Kaydet' : 'Save as new'}
    secondaryLabel={locale === 'tr' ? 'Üzerine Kaydet' : 'Replace original'}
    primaryDisabled={isSaving}
    secondaryDisabled={isSaving}
    primaryLoading={savingMode === 'copy'}
    secondaryLoading={savingMode === 'overwrite'}
    onPrimary={() => { void handleSave('copy') }}
    onSecondary={() => { void handleSave('overwrite') }}
    onClose={() => { if (!isSaving) setSaveDialogOpen(false) }}
    closeOnBackdrop={!isSaving}
  />
  <VoxnelaAlertModal
    open={confirmDiscardOpen}
    title={copy.unsavedChangesTitle}
    message={copy.unsavedChangesMessage}
    primaryLabel={copy.saveChanges}
    secondaryLabel={copy.discardChanges}
    onPrimary={() => {
      setConfirmDiscardOpen(false)
      setSaveDialogOpen(true)
    }}
    onSecondary={() => {
      setConfirmDiscardOpen(false)
      setEdits(emptyEdits)
      onClose()
    }}
    onClose={() => setConfirmDiscardOpen(false)}
  />
  </>
}
